import { NoteIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { CreateReservationNoteRequest } from '@/api/reservations/create-reservation-note'
import { RemoveReservationNoteRequest } from '@/api/reservations/delete-reservation-note'
import { ReservationNote } from '@/api/reservations/get-reservation-notes'
import { UpdateReservationNoteRequest } from '@/api/reservations/update-reservation-note'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Divider } from '@/components/ui/divider'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ } from '@/constants'
import { popupFactory } from '@/factories'
import { UserRoleEnum } from '@/models/features/user.models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserData } from '@/reducers/userDataReducer'
import { formatDateOnly, formatTimeOnly } from '@/utils'

import { reservationEmptyNoteFormValues } from '../reservation-details.constants'
import { NoteFormData } from '../reservation-details.types'
import { getUserRoleTranslation } from '../reservation-details.utils'
import * as S from '../reservations-details.styled'
import { NoteForm } from './NoteForm'

interface Props {
  reservationId?: string
  notes?: ReservationNote[] | null
  onCreate: (variables: CreateReservationNoteRequest) => Promise<unknown>
  onUpdate: (variables: UpdateReservationNoteRequest) => Promise<unknown>
  onRemove: (variables: RemoveReservationNoteRequest) => Promise<unknown>
  isLoading?: boolean
}

export const Notes = ({
  notes = [],
  reservationId,
  onCreate,
  onUpdate,
  onRemove,
  isLoading,
}: Props) => {
  const { t } = useTranslation(['reservations'])
  const userData = useSelector(selectUserData)
  const activeBuilding = useSelector(selectActiveBuilding)
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ
  const noNotes = notes?.length === 0

  const [noteFormValues, setNoteFormValues] = useState<NoteFormData>()

  const buttonsDisabled = isLoading || !!noteFormValues

  const onSubmitForm = async ({ id, title, noteBody }: NoteFormData) => {
    if (!reservationId) {
      return
    }

    if (id) {
      await onUpdate({
        noteBody,
        noteId: id,
        reservationId,
        title,
      })
    } else {
      await onCreate({
        noteBody,
        reservationId,
        title,
      })
    }

    setNoteFormValues(undefined)
  }

  const onRemoveReservation = (noteId: number, noteTitle: string) => {
    if (!reservationId || !noteId) {
      return
    }

    popupFactory.open(ConfirmationPopup, {
      children: t('reservations:details.notes.deleteNodeModalBody', { noteTitle }),
      confirmText: t('reservations:details.notes.deleteNodeModalConfirm'),
      onConfirm: async () => {
        await onRemove({
          noteId,
          reservationId,
        })
      },
      title: t('reservations:details.notes.deleteNodeModalTitle'),
      variant: 'danger',
    })
  }

  return (
    <S.CompactCard>
      <S.BasicInfo>
        <NoteIcon />
        <Typography size="heading-small" weight="bold">
          {t('reservations:details.notes.notes')}
        </Typography>
      </S.BasicInfo>
      {!noteFormValues && (
        <S.AddNewNoteButton
          use="secondary"
          size="small"
          disabled={buttonsDisabled}
          onClick={() => setNoteFormValues(reservationEmptyNoteFormValues)}
        >
          {t('reservations:details.notes.addNewNote')}
          <PlusIcon />
        </S.AddNewNoteButton>
      )}
      <S.NotesContainer>
        {noteFormValues && (
          <NoteForm
            note={noteFormValues}
            closeForm={() => setNoteFormValues(undefined)}
            shouldClearFormOnCancelClick={noNotes}
            onSubmitForm={onSubmitForm}
          />
        )}

        {noNotes ? (
          <S.NoNotesContainer>
            <img src="/images/files-icon.svg" alt="" />
            <Typography color="black800" weight="bold">
              {t('reservations:details.notes.noNotesTitle')}
            </Typography>
            <Typography color="black500">{t('reservations:details.notes.noNotesBody')}</Typography>
          </S.NoNotesContainer>
        ) : (
          notes?.map((note) => {
            const canEditOrDelete =
              userData?.user?.id === note.createdBy.id ||
              userData?.user?.role === UserRoleEnum.enum.Administrator
            const zonedDate = toZonedTime(new Date(note.createdAt), timezone)

            const roleString = getUserRoleTranslation(note.createdBy.role)

            const createdByString = `${note.createdBy.firstName} ${note.createdBy.lastName}, ${roleString}, ${formatDateOnly(zonedDate)} ${formatTimeOnly(zonedDate)}`

            return (
              <S.Note key={note.id}>
                <S.NoteHeader>
                  <Typography size="text-medium" weight="bold">
                    {note.title}
                  </Typography>
                  {canEditOrDelete && (
                    <div>
                      <Button
                        onlyBox
                        use="secondary"
                        size="small"
                        disabled={buttonsDisabled}
                        onClick={() =>
                          setNoteFormValues({
                            id: note.id,
                            noteBody: note.noteBody,
                            title: note.title,
                          })
                        }
                      >
                        <PencilSimpleIcon />
                      </Button>
                      <Button
                        onlyBox
                        use="secondary"
                        size="small"
                        disabled={buttonsDisabled}
                        onClick={() => onRemoveReservation(note.id, note.title)}
                      >
                        <TrashIcon />
                      </Button>
                    </div>
                  )}
                </S.NoteHeader>
                <Typography size="text-medium" weight="medium">
                  {note.noteBody}
                </Typography>
                <Divider color="gray300" />
                <Typography color="primary500" size="text-small">
                  {createdByString}
                </Typography>
              </S.Note>
            )
          })
        )}
      </S.NotesContainer>
    </S.CompactCard>
  )
}
